import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { API } from "../../services/axios";
import { URLS } from "../../services/urls";
import { toast } from "react-toastify";
import { setUserDetails } from "../../components/utils/functions";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/slices/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";
import "./Login.css"; // Assurez-vous de créer ce fichier CSS (voir plus bas)

const Login = () => {
  const [loading, setLoading] = useState(false);
  let [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onSubmit = (data) => {
    setLoading(true);
    API.post(URLS.AUTHS.LOGIN, data)
      .then((resp) => {
        if (
          resp.data.user["user_level"] === "jobseeker" ||
          resp.data.user["user_level"] === "jobseeket" // J'ai gardé ta typo potentielle "jobseeket" au cas où c'est en base
        ) {
          setLoading(false);
          toast.error(t("textLoginError"));
        } else {
          const redirect = window.localStorage.getItem("redirect");
          const redirect_link = redirect?.includes('login') ? "/" : redirect;
          setLoading(false);
          setUserDetails(resp.data);
          dispatch(loginSuccess(resp.data));
          navigate(searchParams.get("redirect") ?? redirect_link ?? "/");
        }
      })
      .catch((error) => {
        setLoading(false);
        toast.error(
          error?.response?.data?.detail ??
            error?.response?.data?.message ??
            t("textLoginError")
        );
      });
  };

  return (
    <section className="login-wrapper">
      <div className="container-fluid h-100">
        <div className="row h-100">
          
          {/* Colonne Gauche : Image / Branding */}
          <div className="col-lg-6 col-md-5 d-none d-md-flex align-items-center justify-content-center login-side-bg">
            <div className="overlay-content text-white text-center">
              <h2 className="display-4 fw-bold mb-4">Bienvenue sur l'ATS</h2>
              <p className="lead px-5">Gérez vos talents, optimisez vos recrutements et collaborez efficacement.</p>
            </div>
          </div>

          {/* Colonne Droite : Formulaire */}
          <div className="col-lg-6 col-md-7 d-flex align-items-center justify-content-center bg-white">
            <div className="login-form-container">
              
              <div className="text-center mb-5">
                <img
                  src="assets/imgs/page/about/logo.png"
                  alt="logo"
                  className="login-logo mb-3"
                />
                <h3 className="fw-bold text-dark">{t("actionLogin")}</h3>
                <p className="text-muted">Connectez-vous pour accéder à votre dashboard</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                
                {/* Email Input */}
                <div className="form-group mb-4">
                  <label className="form-label text-muted fw-semibold" htmlFor="email">
                    {t("email")}
                  </label>
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                        <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    <input
                      className={`form-control bg-light border-start-0 ps-0 ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      type="text"
                      placeholder="exemple@entreprise.com"
                      {...register("email", {
                        required: true,
                        validate: {
                          maxLength: (v) => v.length <= 50 || t("textAtMost"),
                          matchPattern: (v) =>
                            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || t("textValidEmail"),
                        },
                      })}
                    />
                  </div>
                  {errors.email?.message && (
                    <small className="text-danger mt-1 d-block">{errors.email.message}</small>
                  )}
                </div>

                {/* Password Input */}
                <div className="form-group mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label text-muted fw-semibold" htmlFor="password">
                        {t("password")}
                    </label>
                    <Link className="text-primary small text-decoration-none fw-bold" to="/password-reset">
                        {t("forgotPassword")}
                    </Link>
                  </div>
                  
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                        <FontAwesomeIcon icon={faLock} />
                    </span>
                    <input
                      className={`form-control bg-light border-start-0 ps-0 ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      {...register("password", { required: true })}
                    />
                    <span 
                        className="input-group-text bg-light border-start-0 cursor-pointer" 
                        onClick={togglePasswordVisibility}
                        style={{cursor: 'pointer'}}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} className="text-muted"/>
                    </span>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-group mt-5">
                  <button
                    className="btn btn-primary btn-lg w-100 py-3 fw-bold shadow-sm"
                    type="submit"
                    disabled={loading}
                    style={{ borderRadius: '10px' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Connexion en cours...
                      </>
                    ) : (
                      t("actionLogin")
                    )}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;